import React, { useState, useEffect } from 'react';
import { School, Search, Filter, ChevronRight, CheckCircle, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { trackPageView, trackContentInteraction } from '../lib/user-tracking';
import AuthRequiredModal from '../components/AuthRequiredModal';

// Dados simulados dos cursos
const cursosData = [
  {
    id: "fundamentos-fe",
    titulo: "Fundamentos da Fé",
    publico: "jovem",
    descricao: "Introdução à vida cristã para jovens.",
    imagem: "https://images.unsplash.com/photo-1504052434569-70ad5836ab65?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    aulas: 8,
    duracao: "4 semanas"
  },
  {
    id: "crescimento-espiritual",
    titulo: "Crescimento Espiritual",
    publico: "adulto",
    descricao: "Curso voltado ao amadurecimento espiritual.",
    imagem: "https://images.unsplash.com/photo-1529070538774-1843cb3265df?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    aulas: 10,
    duracao: "6 semanas"
  },
  {
    id: "discipulado",
    titulo: "Discipulado",
    publico: "jovem",
    descricao: "Aprenda a ser um discípulo de Jesus e a fazer discípulos.",
    imagem: "https://images.unsplash.com/photo-1490730141103-6cac27aaab94?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    aulas: 6,
    duracao: "3 semanas"
  },
  {
    id: "lideranca-crista",
    titulo: "Liderança Cristã",
    publico: "adulto",
    descricao: "Desenvolva habilidades de liderança baseadas em princípios bíblicos.",
    imagem: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    aulas: 12,
    duracao: "8 semanas"
  },
  {
    id: "evangelismo",
    titulo: "Evangelismo Prático",
    publico: "jovem",
    descricao: "Métodos e estratégias para compartilhar o evangelho de forma eficaz.",
    imagem: "https://images.unsplash.com/photo-1519834089823-af2d966a0648?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    aulas: 5,
    duracao: "3 semanas"
  },
  {
    id: "familia-crista",
    titulo: "Família Cristã",
    publico: "adulto",
    descricao: "Princípios bíblicos para construir uma família saudável e feliz.",
    imagem: "https://images.unsplash.com/photo-1511895426328-dc8714191300?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    aulas: 8,
    duracao: "5 semanas"
  }
];

// Interface para os dados de conclusão de curso
interface CursoCompletado {
  id: string;
  dataCompletado: string;
  pontuacao: number;
}

export default function Cursos() {
  const navigate = useNavigate();
  const [cursosCompletados, setCursosCompletados] = useState<Record<string, CursoCompletado>>({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroPublico, setFiltroPublico] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [selectedCursoId, setSelectedCursoId] = useState<string | null>(null);

  useEffect(() => {
    // Registrar visualização da página
    trackPageView('cursos');
    
    // Verificar autenticação
    checkAuthStatus();
    
    // Carregar dados de cursos completados do localStorage
    const carregarCursosCompletados = async () => {
      try {
        const cursosCompletadosString = localStorage.getItem('cursosCompletados');
        if (cursosCompletadosString) {
          setCursosCompletados(JSON.parse(cursosCompletadosString));
        }
        
        // Se o usuário estiver logado, buscar progresso do banco de dados
        if (isLoggedIn) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const { data: progressData } = await supabase
              .from('course_progress')
              .select('course_id, is_completed, completion_date')
              .eq('user_id', session.user.id);
              
            if (progressData && progressData.length > 0) {
              const dbCursosCompletados: Record<string, CursoCompletado> = {};
              
              progressData.forEach(item => {
                if (item.is_completed) {
                  dbCursosCompletados[item.course_id] = {
                    id: item.course_id,
                    dataCompletado: item.completion_date || new Date().toISOString(),
                    pontuacao: 100 // Valor padrão
                  };
                }
              });
              
              setCursosCompletados(prev => ({...prev, ...dbCursosCompletados}));
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar cursos completados:', error);
      } finally {
        setLoading(false);
      }
    };

    carregarCursosCompletados();
  }, [isLoggedIn]);

  const checkAuthStatus = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setIsLoggedIn(!!session);
  };

  // Filtrar cursos com base na busca e no filtro de público
  const cursosFiltrados = cursosData.filter(curso => {
    const matchesSearch = curso.titulo.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         curso.descricao.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPublico = filtroPublico ? curso.publico === filtroPublico : true;
    return matchesSearch && matchesPublico;
  });

  // Separar cursos por público
  const cursosJovens = cursosFiltrados.filter(curso => curso.publico === 'jovem');
  const cursosAdultos = cursosFiltrados.filter(curso => curso.publico === 'adulto');

  const handleCursoClick = (cursoId: string) => {
    if (!isLoggedIn) {
      setSelectedCursoId(cursoId);
      setIsAuthModalOpen(true);
      return;
    }
    
    // Registrar interação com o curso
    trackContentInteraction('course', cursoId, 'view');
    navigate(`/cursos/${cursoId}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <School className="h-8 w-8 text-brand-teal" />
          <h1 className="text-3xl font-bold text-brand-teal">Cursos Bíblicos</h1>
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
        >
          <Home className="h-4 w-4" />
          <span>Voltar para Home</span>
        </button>
      </div>

      <div className="mb-8">
        <p className="text-gray-600 mb-6">
          Aprofunde seu conhecimento bíblico com nossos cursos online gratuitos. Estude no seu próprio ritmo e receba um certificado ao concluir cada curso.
        </p>

        {/* Filtros e Busca */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar cursos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFiltroPublico(null)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filtroPublico === null
                  ? 'bg-brand-teal text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todos
            </button>
            <button
              onClick={() => setFiltroPublico('jovem')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filtroPublico === 'jovem'
                  ? 'bg-brand-teal text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Jovens
            </button>
            <button
              onClick={() => setFiltroPublico('adulto')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filtroPublico === 'adulto'
                  ? 'bg-brand-teal text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Adultos
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Cursos para Jovens */}
          {(filtroPublico === null || filtroPublico === 'jovem') && cursosJovens.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-indigo-600 mb-4">Cursos para Jovens</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {cursosJovens.map((curso) => (
                  <div 
                    key={curso.id}
                    className="bg-white rounded-xl overflow-hidden shadow-elevation-1 hover:shadow-elevation-2 transition-shadow"
                  >
                    <div className="relative h-48">
                      <img 
                        src={curso.imagem} 
                        alt={curso.titulo} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 right-3 bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-xs font-medium">
                        Jovem
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{curso.titulo}</h3>
                      <p className="text-gray-600 mb-4">{curso.descricao}</p>
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <School className="h-4 w-4" />
                            {curso.aulas} aulas
                          </span>
                          <span>•</span>
                          <span>{curso.duracao}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleCursoClick(curso.id)}
                        className={`w-full py-2 px-4 rounded-lg flex items-center justify-center gap-2 ${
                          cursosCompletados[curso.id]
                            ? 'bg-green-500 text-white'
                            : 'bg-brand-teal text-white hover:bg-brand-teal/90'
                        } transition-colors`}
                      >
                        {cursosCompletados[curso.id] ? (
                          <>
                            <CheckCircle className="h-4 w-4" />
                            <span>Curso Concluído</span>
                          </>
                        ) : (
                          <>
                            <ChevronRight className="h-4 w-4" />
                            <span>Acessar Curso</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cursos para Adultos */}
          {(filtroPublico === null || filtroPublico === 'adulto') && cursosAdultos.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-indigo-600 mb-4">Cursos para Adultos</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {cursosAdultos.map((curso) => (
                  <div 
                    key={curso.id}
                    className="bg-white rounded-xl overflow-hidden shadow-elevation-1 hover:shadow-elevation-2 transition-shadow"
                  >
                    <div className="relative h-48">
                      <img 
                        src={curso.imagem} 
                        alt={curso.titulo} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 right-3 bg-indigo-100 text-indigo-600 px-3 py-1 rounded-full text-xs font-medium">
                        Adulto
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{curso.titulo}</h3>
                      <p className="text-gray-600 mb-4">{curso.descricao}</p>
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <School className="h-4 w-4" />
                            {curso.aulas} aulas
                          </span>
                          <span>•</span>
                          <span>{curso.duracao}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleCursoClick(curso.id)}
                        className={`w-full py-2 px-4 rounded-lg flex items-center justify-center gap-2 ${
                          cursosCompletados[curso.id]
                            ? 'bg-green-500 text-white'
                            : 'bg-brand-teal text-white hover:bg-brand-teal/90'
                        } transition-colors`}
                      >
                        {cursosCompletados[curso.id] ? (
                          <>
                            <CheckCircle className="h-4 w-4" />
                            <span>Curso Concluído</span>
                          </>
                        ) : (
                          <>
                            <ChevronRight className="h-4 w-4" />
                            <span>Acessar Curso</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {cursosFiltrados.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm">
              <School className="h-16 w-16 text-brand-teal/20 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-brand-teal mb-2">
                Nenhum curso encontrado
              </h2>
              <p className="text-gray-600 max-w-md mx-auto">
                {searchTerm
                  ? 'Não encontramos resultados para sua busca. Tente outros termos ou remova os filtros.'
                  : 'Não há cursos disponíveis para os filtros selecionados.'}
              </p>
              {(searchTerm || filtroPublico) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFiltroPublico(null);
                  }}
                  className="mt-4 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
                >
                  Limpar filtros
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal de autenticação necessária */}
      <AuthRequiredModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        message="Para acessar os cursos bíblicos, você precisa estar logado."
        returnTo={selectedCursoId ? `/cursos/${selectedCursoId}` : '/cursos'}
      />
    </div>
  );
}